import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import useCurrency from "../../../../../hooks/useCurrency";
import { storeAnalyticsSelector } from "../../../../../redux/slices/store_analytics";

import { CampaignMiniChart } from "./BudgetChartView";

const FAMILY_LABEL = {
  BUDGET: "Budget",
  CPC: "CPC",
  EFFICIENCY: "Efficiency",
  AMAZON_DIAGNOSTIC: "Amazon",
};

const FAMILY_COLOR = {
  BUDGET: "#3b82f6",
  CPC: "#8b5cf6",
  EFFICIENCY: "#10b981",
  AMAZON_DIAGNOSTIC: "#6b7280",
};

const fmtMoney = (v, symbol) =>
  `${symbol}${(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Field = ({ label, value, mono }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 600, letterSpacing: "0.04em" }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={700}
      sx={{ fontFamily: mono ? "monospace" : undefined, mt: 0.5 }}
    >
      {value}
    </Typography>
  </Box>
);

const MetricBlock = ({ alert }) => {
  const { marketplaceCurrencySymbol } = useCurrency();
  if (!alert.metric) return null;
  const { name, value, baseline, unit } = alert.metric;
  const isRatio = unit === "ratio" || unit === "zscore";
  const fmt = (v) => {
    if (v == null) return "—";
    if (unit === "USD") return fmtMoney(v, marketplaceCurrencySymbol);
    if (unit === "ratio") return Number(v).toFixed(2) + "×";
    if (unit === "zscore") return Number(v).toFixed(2);
    if (unit === "hours") return Number(v).toFixed(1) + "h";
    return v.toLocaleString();
  };
  return (
    <Stack direction="row" spacing={3} sx={{ p: 2 }}>
      <Field label={name || "Metric"} value={fmt(value)} mono />
      {baseline != null && (
        <Field label="Baseline" value={fmt(baseline)} mono />
      )}
      {isRatio && (
        <Field
          label="Δ"
          value={
            value != null && baseline != null
              ? `${(
                  ((value - baseline) / Math.max(Math.abs(baseline), 0.0001)) *
                  100
                ).toFixed(0)}%`
              : "—"
          }
          mono
        />
      )}
    </Stack>
  );
};

const BudgetDrilldown = ({ alert }) => {
  const { marketplaceCurrencySymbol } = useCurrency();
  const { ppcAlertsData } = useSelector(storeAnalyticsSelector);
  const campaignRow = useMemo(() => {
    const rows = Array.isArray(ppcAlertsData) ? ppcAlertsData : [];
    return rows.find((r) => String(r.campaign_id) === String(alert.campaignId));
  }, [ppcAlertsData, alert.campaignId]);

  if (!campaignRow) {
    return (
      <Box sx={{ p: 3, color: "text.secondary" }}>
        <Typography variant="body2">
          Budget time series not loaded for this campaign. Switch to the budget
          view to populate it, or refresh.
        </Typography>
      </Box>
    );
  }

  const lastPct =
    campaignRow.usage_percentages?.[campaignRow.usage_percentages.length - 1] ??
    0;
  const lastDollars =
    campaignRow.usage_dollars?.[campaignRow.usage_dollars.length - 1] ?? 0;

  return (
    <Box>
      <Stack direction="row" spacing={3} sx={{ p: 2 }}>
        <Field
          label="Daily budget"
          value={fmtMoney(campaignRow.budget, marketplaceCurrencySymbol)}
          mono
        />
        <Field
          label="Spent so far"
          value={fmtMoney(lastDollars, marketplaceCurrencySymbol)}
          mono
        />
        <Field label="% used" value={`${lastPct.toFixed(1)}%`} mono />
      </Stack>
      <Divider />
      <Box sx={{ p: 2, height: 280 }}>
        <CampaignMiniChart
          campaign={campaignRow}
          color={FAMILY_COLOR.BUDGET}
          mode="dollar"
        />
      </Box>
    </Box>
  );
};

const NonBudgetDrilldown = ({ alert }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Drilldown chart for {FAMILY_LABEL[alert.family] || alert.family} alerts
      coming next. Below is the alert's underlying metric.
    </Typography>
    <MetricBlock alert={alert} />
  </Box>
);

const AmazonDrilldown = ({ alert }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Amazon's own recommendation for this campaign:
    </Typography>
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="body2" sx={{ fontStyle: "italic" }}>
        "{alert.message}"
      </Typography>
    </Paper>
    {alert.externalLink && (
      <Button
        variant="contained"
        startIcon={<LaunchIcon />}
        href={alert.externalLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open in Amazon Ads
      </Button>
    )}
  </Box>
);

export default function AlertDrawer({ open, alert, onClose }) {
  if (!alert) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }
  const family = alert.family;
  const familyColor = FAMILY_COLOR[family] || "#6b7280";

  let Drilldown;
  if (family === "BUDGET") Drilldown = BudgetDrilldown;
  else if (family === "AMAZON_DIAGNOSTIC") Drilldown = AmazonDrilldown;
  else Drilldown = NonBudgetDrilldown;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 560 } } }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Chip
            size="small"
            label={FAMILY_LABEL[family] || family}
            sx={{
              bgcolor: familyColor + "18",
              color: familyColor,
              fontWeight: 700,
            }}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          {alert.campaignName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {alert.adProduct} · rule {alert.ruleId} ·{" "}
          <strong>${alert.dollarsAtRisk?.toFixed(2)}</strong> at risk
        </Typography>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {alert.message}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: "text.secondary", fontStyle: "italic" }}
        >
          → {alert.recommendedAction}
        </Typography>
      </Box>
      <Drilldown alert={alert} />
    </Drawer>
  );
}
